import {useMemo} from 'react';
import {ArrowRight, Calculator, Check, Store, Trash2, X} from 'lucide-react';
import {Picture} from '../components/Picture';
import {money, number} from '../lib/format';
import {useAppLink} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {
  DIRECTION,
  FURNISHING,
  LEGAL,
  LISTING_TYPE,
  coverPhoto,
  has360,
  pricePerM2,
  type Listing,
} from '../store/types';
import {Button, EmptyState, Screen, Toolbar} from '../ui/primitives';

/** A row of the comparison: how to read a value, and which way is "better". */
interface Row {
  label: string;
  value: (listing: Listing) => string;
  /** Higher is better, lower is better, or no winner at all. */
  best?: 'low' | 'high';
  score?: (listing: Listing) => number;
}

const ROWS: Row[] = [
  {best: 'low', label: 'Giá', score: (listing) => listing.price, value: (listing) => money(listing.price) + (listing.purpose === 'cho-thue' ? '/tháng' : '')},
  {best: 'low', label: 'Giá / m²', score: pricePerM2, value: (listing) => money(pricePerM2(listing))},
  {best: 'high', label: 'Diện tích', score: (listing) => listing.area, value: (listing) => `${number(listing.area)} m²`},
  {best: 'high', label: 'Phòng ngủ', score: (listing) => listing.bedrooms, value: (listing) => (listing.bedrooms > 0 ? `${listing.bedrooms}` : '—')},
  {best: 'high', label: 'Phòng tắm', score: (listing) => listing.bathrooms, value: (listing) => (listing.bathrooms > 0 ? `${listing.bathrooms}` : '—')},
  {label: 'Loại hình', value: (listing) => LISTING_TYPE[listing.type]},
  {label: 'Khu vực', value: (listing) => `${listing.district}, ${listing.province}`},
  {label: 'Hướng', value: (listing) => DIRECTION[listing.direction]},
  {label: 'Pháp lý', value: (listing) => LEGAL[listing.legal]},
  {label: 'Nội thất', value: (listing) => FURNISHING[listing.furnishing]},
  {best: 'high', label: 'Tiện ích', score: (listing) => listing.amenities.length, value: (listing) => `${listing.amenities.length} tiện ích`},
  {best: 'high', label: 'Ảnh 360°', score: (listing) => (has360(listing) ? 1 : 0), value: (listing) => (has360(listing) ? 'Có' : 'Không')},
  {best: 'high', label: 'Lượt xem', score: (listing) => listing.views, value: (listing) => number(listing.views)},
];

/**
 * So sánh — the shortlist, side by side.
 *
 * A comparison is a table and stays one at every width: a phone gets the same
 * table with a frozen label column and horizontal scroll, because folding it
 * into cards would put the two flats a buyer is deciding between on different
 * screens, which is precisely the comparison they came here to make.
 *
 * "Tốt nhất" marks are only drawn where better is actually defined — cheaper,
 * larger, more rooms. No badge is claimed for a direction or a legal status.
 */
export function CompareApp() {
  const {clearCompare, compare, listings, toggleCompare} = useStore();
  const {open} = useAppLink();

  const picked = useMemo(
    () =>
      compare
        .map((id) => listings.find((listing) => listing.id === id))
        .filter((listing): listing is Listing => Boolean(listing)),
    [compare, listings],
  );

  const winners = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of ROWS) {
      if (!row.best || !row.score || picked.length < 2) {
        map.set(row.label, null);
        continue;
      }
      const scores = picked.map((listing) => row.score!(listing));
      const target = row.best === 'low' ? Math.min(...scores) : Math.max(...scores);
      // A tie has no winner: two identical numbers with one badge between them
      // would read as a difference that is not there.
      const hits = scores.filter((value) => value === target).length;
      map.set(row.label, hits === 1 ? picked[scores.indexOf(target)]!.id : null);
    }
    return map;
  }, [picked]);

  if (picked.length === 0) {
    return (
      <Screen>
        <EmptyState
          title="Chưa có tin nào để so sánh"
          hint="Ở app Thị trường, bấm “So sánh” trên tin bạn quan tâm — tối đa 4 tin sẽ hiện ở đây."
        />
        <div className="mt-4 flex justify-center">
          <Button tone="primary" icon={<Store size={15} />} onClick={() => open('market')}>
            Mở Thị trường
          </Button>
        </div>
      </Screen>
    );
  }

  const cheapest = [...picked].sort((a, b) => pricePerM2(a) - pricePerM2(b))[0]!;

  return (
    <Screen
      toolbar={
        <Toolbar>
          <span className="text-[12.5px] font-semibold text-ink-2">
            Đang so sánh {picked.length} tin
          </span>
          <span className="flex-1" />
          <Button icon={<Store size={15} />} onClick={() => open('market')}>
            Thêm tin
          </Button>
          <Button icon={<Trash2 size={15} />} onClick={clearCompare}>
            Xoá hết
          </Button>
        </Toolbar>
      }
    >
      {picked.length > 1 ? (
        <p className="surface mb-4 rounded-card p-4 text-[13px] text-ink-2">
          Rẻ nhất theo giá mỗi m²:{' '}
          <span className="font-bold text-ink">{cheapest.title}</span> —{' '}
          <span className="font-bold text-accent">{money(pricePerM2(cheapest))}/m²</span>
        </p>
      ) : null}

      <div className="sunken overflow-x-auto rounded-card">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 w-[120px] bg-[var(--color-sunken)] px-4 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-3">
                Tiêu chí
              </th>
              {picked.map((listing) => (
                <th key={listing.id} className="min-w-[200px] px-3 pb-3 pt-4 align-top text-left">
                  <div className="relative">
                    <button
                      type="button"
                      aria-label="Bỏ khỏi so sánh"
                      onClick={() => toggleCompare(listing.id)}
                      className="btn absolute -right-1 -top-1 z-10 flex h-8 w-8 items-center justify-center rounded-full"
                    >
                      <X size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => open('market', {listingId: listing.id})}
                      className="block w-full text-left"
                    >
                      <Picture
                        alt={listing.title}
                        className="rounded-control"
                        ratio="4 / 3"
                        refItem={coverPhoto(listing)?.ref ?? null}
                      />
                      <p className="mt-2 line-clamp-2 text-[13px] font-bold leading-snug text-ink">
                        {listing.title}
                      </p>
                      <p className="mt-1 text-[11px] font-medium text-ink-3">{listing.code}</p>
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} className="border-t border-line-soft">
                <th className="sticky left-0 z-10 bg-[var(--color-sunken)] px-4 py-3 text-left text-[11.5px] font-semibold text-ink-3">
                  {row.label}
                </th>
                {picked.map((listing) => {
                  const isBest = winners.get(row.label) === listing.id;
                  return (
                    <td
                      key={listing.id}
                      className={`px-3 py-3 align-middle tabular-nums ${
                        isBest ? 'font-bold text-ink' : 'text-ink-2'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {row.value(listing)}
                        {isBest ? (
                          <span className="chip tone-emerald inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold">
                            <Check size={11} /> Tốt nhất
                          </span>
                        ) : null}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr className="border-t border-line-soft">
              <th className="sticky left-0 z-10 bg-[var(--color-sunken)] px-4 py-3" />
              {picked.map((listing) => (
                <td key={listing.id} className="px-3 py-3">
                  <div className="flex flex-col gap-2">
                    <Button
                      icon={<ArrowRight size={14} />}
                      onClick={() => open('market', {listingId: listing.id})}
                    >
                      Xem chi tiết
                    </Button>
                    <Button
                      icon={<Calculator size={14} />}
                      onClick={() => open('loan', {label: listing.title, price: listing.price})}
                    >
                      Tính vay
                    </Button>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </Screen>
  );
}
